import { useCallback, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

// PM Analitik Faz 2 (spec §6.2) — İK Operasyon Panosu ve E02'nin ortak süzgeç durumu. Durum URL sorgu dizesinde yaşar
// (yenileme ve detaydan geri dönüşte korunur); analitik uçlarına giden parametreler toAnalyticsParams'tan üretilir
// (PmAnalyticsFilter.Parse sözleşmesi: periodId[eq], departmentId[in], managerEmployeeId[eq], includeDescendants,
// locationId[in], titleId[in], groupId[in], gradeId[in], jobFamilyId[in], participantStatus[in], orgView). Kademe ve iş ailesi
// süzgeci sunucuda yalnız İK'ya açıktır (spec 2026-10-27 §7.6); şerit de yalnız İK'ya gösterir.

export const PARTICIPANT_STATUSES = ['active', 'excluded', 'left'];
export const ORG_VIEWS = ['snapshot', 'current'];
export const COMPARE_NONE = 'none';

// URL anahtarları kısa tutulur; varsayılan değerler URL'ye yazılmaz.
const Q = {
  period: 'period', departments: 'dept', locations: 'loc', manager: 'mgr', descendants: 'sub', titles: 'title', groups: 'group',
  grades: 'grade', jobFamilies: 'jfam', statuses: 'pstatus', orgView: 'org', compare: 'cmp',
};

const list = (v) => (v ? v.split(',').map((x) => x.trim()).filter(Boolean) : []);

export const EMPTY_FILTERS = Object.freeze({
  periodId: null,
  departmentIds: [],
  locationIds: [],
  managerEmployeeId: null,
  includeDescendants: true,
  titleIds: [],
  groupIds: [],
  gradeIds: [],
  jobFamilyIds: [],
  participantStatuses: [],
  orgView: 'snapshot',
  comparePeriodId: null,   // null = varsayılan (otomatik), 'none' = karşılaştırma yok, kimlik = seçilen dönem
});

export function parseFilters(search) {
  const p = new URLSearchParams(search || '');
  const orgView = p.get(Q.orgView);
  return {
    periodId: p.get(Q.period) || null,
    departmentIds: list(p.get(Q.departments)),
    locationIds: list(p.get(Q.locations)),
    managerEmployeeId: p.get(Q.manager) || null,
    includeDescendants: p.get(Q.descendants) !== '0',
    titleIds: list(p.get(Q.titles)),
    groupIds: list(p.get(Q.groups)),
    gradeIds: list(p.get(Q.grades)),
    jobFamilyIds: list(p.get(Q.jobFamilies)),
    participantStatuses: list(p.get(Q.statuses)).filter((s) => PARTICIPANT_STATUSES.includes(s)),
    orgView: ORG_VIEWS.includes(orgView) ? orgView : 'snapshot',
    comparePeriodId: p.get(Q.compare) || null,
  };
}

// Mevcut sorgu dizesindeki süzgeç dışı anahtarlar (ör. E02 sekmesi) korunur.
export function serializeFilters(filters, baseSearch = '') {
  const p = new URLSearchParams(baseSearch || '');
  Object.values(Q).forEach((k) => p.delete(k));
  const f = { ...EMPTY_FILTERS, ...filters };
  if (f.periodId) p.set(Q.period, f.periodId);
  if (f.departmentIds.length) p.set(Q.departments, f.departmentIds.join(','));
  if (f.locationIds.length) p.set(Q.locations, f.locationIds.join(','));
  if (f.managerEmployeeId) p.set(Q.manager, f.managerEmployeeId);
  if (f.managerEmployeeId && !f.includeDescendants) p.set(Q.descendants, '0');
  if (f.titleIds.length) p.set(Q.titles, f.titleIds.join(','));
  if (f.groupIds.length) p.set(Q.groups, f.groupIds.join(','));
  if (f.gradeIds.length) p.set(Q.grades, f.gradeIds.join(','));
  if (f.jobFamilyIds.length) p.set(Q.jobFamilies, f.jobFamilyIds.join(','));
  if (f.participantStatuses.length) p.set(Q.statuses, f.participantStatuses.join(','));
  if (f.orgView !== 'snapshot') p.set(Q.orgView, f.orgView);
  if (f.comparePeriodId) p.set(Q.compare, f.comparePeriodId);
  const s = p.toString();
  return s ? `?${s}` : '';
}

export function toAnalyticsParams(filters, periodId = filters?.periodId) {
  const f = { ...EMPTY_FILTERS, ...filters };
  const params = {};
  if (periodId) params['periodId[eq]'] = periodId;
  if (f.departmentIds.length) params['departmentId[in]'] = f.departmentIds.join(',');
  if (f.locationIds.length) params['locationId[in]'] = f.locationIds.join(',');
  if (f.managerEmployeeId) {
    params['managerEmployeeId[eq]'] = f.managerEmployeeId;
    params.includeDescendants = String(!!f.includeDescendants);
  }
  if (f.titleIds.length) params['titleId[in]'] = f.titleIds.join(',');
  if (f.groupIds.length) params['groupId[in]'] = f.groupIds.join(',');
  if (f.gradeIds.length) params['gradeId[in]'] = f.gradeIds.join(',');
  if (f.jobFamilyIds.length) params['jobFamilyId[in]'] = f.jobFamilyIds.join(',');
  if (f.participantStatuses.length) params['participantStatus[in]'] = f.participantStatuses.join(',');
  if (f.orgView !== 'snapshot') params.orgView = f.orgView;
  return params;
}

// Kapsam süzgeci (dönem ve karşılaştırma dışındaki) etkin mi — yalnız dönem kabul eden yüzeyler "tüm şirket" notu gösterir.
export function hasScopeFilters(filters) {
  return activeFilterKeys(filters).some((k) => k !== 'comparePeriodId');
}

// Etkin (varsayılandan farklı) süzgeçler — şeritteki silinebilir etiketler. Dönem etiket değildir (temizlenemez).
export function activeFilterKeys(filters) {
  const f = { ...EMPTY_FILTERS, ...filters };
  const keys = [];
  if (f.departmentIds.length) keys.push('departmentIds');
  if (f.locationIds.length) keys.push('locationIds');
  if (f.managerEmployeeId) keys.push('managerEmployeeId');
  if (f.titleIds.length) keys.push('titleIds');
  if (f.groupIds.length) keys.push('groupIds');
  if (f.gradeIds.length) keys.push('gradeIds');
  if (f.jobFamilyIds.length) keys.push('jobFamilyIds');
  if (f.participantStatuses.length) keys.push('participantStatuses');
  if (f.orgView !== 'snapshot') keys.push('orgView');
  if (f.comparePeriodId) keys.push('comparePeriodId');
  return keys;
}

export function clearFilter(filters, key) {
  const next = { ...EMPTY_FILTERS, ...filters, [key]: EMPTY_FILTERS[key] };
  if (key === 'managerEmployeeId') next.includeDescendants = true;
  return next;
}

const dayOf = (v) => (v ? String(v).slice(0, 10) : '');

// Spec §6.2 — karşılaştırma dönemi varsayılanı: seçili dönemden ÖNCEKİ (bitişi seçili dönemin başından önce olan), kapanmış en
// yakın yerel ("native") dönem. Kapanış tarihine bakılmaz: yıllık döngüde önceki dönem kalibrasyon ve yayın yüzünden genelde yeni
// dönem başladıktan haftalar sonra kapanır (son inceleme, Önemli 1) — kapanışa bakmak "2026 vs 2025" yerine 2024'ü seçerdi.
export function defaultComparePeriod(periods, selected) {
  if (!selected) return null;
  const start = dayOf(selected.startDate);
  const candidates = (periods || []).filter((p) => p.id !== selected.id
    && (p.origin || 'native') === 'native'
    && p.status === 'closed'
    && dayOf(p.endDate) < start);
  candidates.sort((a, b) => dayOf(b.endDate).localeCompare(dayOf(a.endDate)));
  return candidates[0] || null;
}

export function resolveComparePeriod(periods, filters) {
  const selected = (periods || []).find((p) => p.id === filters.periodId) || null;
  if (filters.comparePeriodId === COMPARE_NONE) return null;
  if (filters.comparePeriodId) return (periods || []).find((p) => p.id === filters.comparePeriodId) || null;
  return defaultComparePeriod(periods, selected);
}

// Varsayılan dönem: en yeni başlangıçlı dönem (liste sunucudan startDate azalan gelir; yine de burada sıralanır).
export function latestPeriod(periods) {
  return [...(periods || [])].sort((a, b) => dayOf(b.startDate).localeCompare(dayOf(a.startDate)))[0] || null;
}

// Kademe / iş ailesi süzgeci yalnız İK (sunucuda 403). İK'nın paylaştığı bir bağlantıyı açan yönetici bu anahtarları
// isteğe taşımasın — uyarı yerine süzgeçsiz görünüm (son inceleme M8).
export function withoutHrOnlyFilters(filters) {
  return { ...filters, gradeIds: [], jobFamilyIds: [] };
}

export default function usePmAnalyticsFilters() {
  const location = useLocation();
  const history = useHistory();
  const { isHr = false } = useAuth() || {};
  const filters = useMemo(() => {
    const parsed = parseFilters(location.search);
    return isHr ? parsed : withoutHrOnlyFilters(parsed);
  }, [location.search, isHr]);

  const setFilters = useCallback((next) => {
    const value = typeof next === 'function' ? next(parseFilters(location.search)) : next;
    history.replace({ pathname: location.pathname, search: serializeFilters(value, location.search) });
  }, [history, location.pathname, location.search]);

  // "Sıfırla" rolün varsayılanına döner: dönem korunur (zorunlu ve temizlenemez), diğer süzgeçler kalkar.
  const reset = useCallback(() => {
    setFilters((f) => ({ ...EMPTY_FILTERS, periodId: f.periodId }));
  }, [setFilters]);

  return { filters, setFilters, reset };
}
